import { AspectRatio, Button, Flex, Heading, Hide, Stack } from '@chakra-ui/react'
import type { CampaignEntity, ComponentCampaignContents } from '@twlvxtwlv/cms'
import { toCmsMediaFileUrl } from '@twlvxtwlv/cms'
import { useTranslation } from 'next-i18next'
import Image from 'next/image'
import { useEffect, useState } from 'react'
import { TokenContent } from '../token-content/token-content'

export type AboutProps = {
  campaignEntity: CampaignEntity
}

export function About({ campaignEntity }: AboutProps) {
  const { t } = useTranslation(['common', 'nft-details', 'campaign', 'checkout'])
  const [amountOfContentShown, setAmountOfContentShown] = useState<number>(2)
  const [contents, setContents] = useState<ComponentCampaignContents[]>([])

  useEffect(() => {
    if (campaignEntity?.attributes?.campaignContents?.contents) {
      const updatedContents = campaignEntity.attributes.campaignContents.contents
        ?.map((content) => content)
        .filter((content) => content) as ComponentCampaignContents[]
      setContents(updatedContents)
    }
  }, [campaignEntity])

  const campaignContents = campaignEntity?.attributes?.campaignContents
  if (!campaignContents) {
    return null
  }

  const campaignContentImageUrl =
    toCmsMediaFileUrl(campaignContents.image?.data?.attributes?.url) ?? null

  const campaignContentImageAlt =
    campaignContents.image?.data?.attributes?.alternativeText ?? undefined

  const showMoreClickHandler = () => {
    setAmountOfContentShown(contents.length)
  }

  return (
    <Stack overflow="hidden" w="full" flexGrow="1" h="full">
      <Stack spacing={0} gap={12} w="full" h="full">
        <Stack spacing={0} gap={4}>
          <Flex direction={{ base: 'column', xl: 'row' }} gap={4} w="full" h="full">
            <Stack w="full" spacing={0} flex={1} gap={4}>
              <Heading as="h2" textTransform="uppercase" fontSize="xl">
                {campaignContents.headline}
              </Heading>
              <Hide below="2xl">
                <Heading as="h3" fontSize={{ base: 'sm', md: 'md' }} textTransform="uppercase">
                  {campaignContents.subHeadline}
                </Heading>
              </Hide>
            </Stack>

            {campaignContentImageUrl ? (
              <AspectRatio maxW="400px" ratio={1} flex={1}>
                <Image
                  src={campaignContentImageUrl}
                  alt={campaignContentImageAlt ?? ''}
                  width={400}
                  height={400}
                  style={{
                    objectFit: 'contain',
                  }}
                />
              </AspectRatio>
            ) : null}
          </Flex>

          <Hide above="xl">
            <Heading as="h3" fontSize={{ base: 'sm', md: 'md' }} textTransform="uppercase">
              {campaignContents.subHeadline}
            </Heading>
          </Hide>
        </Stack>

        <Stack gap={12} h="full">
          {contents
            .filter(Boolean)
            .slice(0, amountOfContentShown)
            .map((content, index) => (
              <TokenContent
                key={index}
                description={content.description ?? undefined}
                headline={content.headline ?? undefined}
                imageAlt={content.image?.data?.attributes?.alternativeText ?? undefined}
                imageUrl={content.image?.data?.attributes?.url}
              />
            ))}
        </Stack>
      </Stack>

      {contents.length > amountOfContentShown ? (
        <Flex justifyContent="center" alignItems="center">
          <Button onClick={showMoreClickHandler} variant="txtBlack">
            {t('campaign:show-more')}
          </Button>
        </Flex>
      ) : null}
    </Stack>
  )
}
